import React from 'react';
import { toast, ToastOptions } from 'react-toastify';

import { Toaster } from '@ui-core';
import { ToasterType, ToastParams } from '@ui-core/types';

import { getToastId } from './toast-service.util';
import { ToastMessageId } from './toast.types';

export class ToastService {
  private static readonly getDefaultOptions = (
    toastParams: ToastParams,
    options?: ToastOptions<unknown>
  ): ToastOptions<unknown> => ({
    className: 'caption-2 mx-2',
    autoClose: 4000,
    closeButton: false,
    pauseOnHover: false,
    hideProgressBar: true,
    position: 'top-center',
    draggableDirection: 'y',
    icon: false,
    toastId: getToastId(toastParams, options),
  });

  private static createCustomToastContent(
    toastParams: ToastParams,
    toasterType: ToasterType
  ): JSX.Element {
    if (typeof toastParams === 'string') {
      return React.createElement(Toaster, {
        message: toastParams,
        type: toasterType,
      });
    }

    return React.createElement(Toaster, {
      ...toastParams,
      type: toasterType,
    });
  }

  public static Success(
    toastParams: ToastParams,
    options?: ToastOptions<unknown>
  ): ToastMessageId {
    const customToastContent = this.createCustomToastContent(
      toastParams,
      'success'
    );

    return toast.success(customToastContent, {
      ...this.getDefaultOptions(toastParams, options),
      ...options,
    });
  }

  public static Info(
    toastParams: ToastParams,
    options?: ToastOptions<unknown>
  ): ToastMessageId {
    const customToastContent = this.createCustomToastContent(
      toastParams,
      'info'
    );
    return toast.info(customToastContent, {
      ...this.getDefaultOptions(toastParams, options),
      ...options,
    });
  }

  public static Warning(
    toastParams: ToastParams,
    options?: ToastOptions<unknown>
  ): ToastMessageId {
    const customToastContent = this.createCustomToastContent(
      toastParams,
      'warning'
    );

    return toast.warning(customToastContent, {
      ...this.getDefaultOptions(toastParams, options),
      ...options,
    });
  }

  public static Error(
    toastParams: ToastParams,
    options?: ToastOptions<unknown>
  ): ToastMessageId {
    const customToastContent = this.createCustomToastContent(
      toastParams,
      'error'
    );

    return toast.error(customToastContent, {
      ...this.getDefaultOptions(toastParams, options),
      ...options,
    });
  }

  public static Update(
    messageId: ToastMessageId,
    type: ToasterType,
    toastParams: ToastParams,
    options?: ToastOptions<unknown>
  ): void {
    const customToastContent = this.createCustomToastContent(toastParams, type);

    toast.update(messageId, {
      render: customToastContent,
      type: type,
      ...options,
    });
  }

  public static IsActive(id: ToastMessageId): boolean {
    return toast.isActive(id);
  }

  public static Dismiss(id: ToastMessageId): void {
    toast.dismiss(id);
  }

  public static DismissAll(): void {
    toast.dismiss();
  }
}
